import { useState, type FormEvent } from "react";
import { api, ApiError } from "../api";
import { Button, Notice } from "../components";
import { Link, useRouter } from "../router";
import { useTenant } from "../shell";
import { SEX_LABEL } from "./patientLabels";

interface Row { id: string; uhid: string; fullName: string; sex: string; age: string; mobile: string | null }

export function Patients() {
  const { can } = useTenant();
  const { navigate } = useRouter();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<Row[] | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function search(e: FormEvent) {
    e.preventDefault(); setNote(null);
    if (q.trim().length < 2) { setNote("Type at least 2 characters."); return; }
    setBusy(true);
    try {
      const r = await api.authed("/api/v1/patients/search", { body: { q } });
      if (r.kind === "invalid_uhid") setNote("That UHID has a typo. Check the number on the patient's card.");
      setRows(r.items);
    } catch (ex) { setNote((ex as ApiError).message); } finally { setBusy(false); }
  }

  return (
    <>
      <div className="page-head">
        <h1>Patients</h1>
        {can("patient:create") && <Link to="/app/patients/new" className="btn btn-primary">Register patient</Link>}
      </div>
      <form className="search-bar" onSubmit={search} role="search">
        <label htmlFor="psearch">Search by name, mobile number, last 4 digits of mobile, or UHID</label>
        <div className="search-row">
          <input id="psearch" value={q} onChange={(e) => setQ(e.target.value)} autoFocus autoComplete="off" spellCheck={false} />
          <Button type="submit" busy={busy}>Search</Button>
        </div>
      </form>
      {note && <Notice tone="info">{note}</Notice>}
      {rows && rows.length === 0 && !note && (
        <div className="empty">
          <p>No patients match “{q}”. Check the spelling or try the mobile number.</p>
          {can("patient:create") && <Link to="/app/patients/new" className="btn btn-secondary">Register a new patient</Link>}
        </div>
      )}
      {rows && rows.length > 0 && (
        <table className="data clickable">
          <thead><tr><th scope="col">UHID</th><th scope="col">Name</th><th scope="col">Age / sex</th><th scope="col">Mobile</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.id} onClick={() => navigate(`/app/patients/${r.id}`)}>
              <td className="mono">{r.uhid}</td>
              <td><Link to={`/app/patients/${r.id}`}>{r.fullName}</Link></td>
              <td>{r.age}, {SEX_LABEL[r.sex]}</td>
              <td className="mono">{r.mobile ?? "—"}</td>
            </tr>))}
          </tbody>
        </table>
      )}
    </>
  );
}
